import { t } from "@lingui/core/macro";
import { format } from "date-fns";

import { LoyaltyIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

export type LoyaltyPointExpiration = {
  readonly id: string;
  readonly points: number;
  readonly expiresAt: string;
};

type ExpiringPointsScheduleProps = {
  expirations: readonly LoyaltyPointExpiration[];
  pointTerminologyOther: string;
  timeZone: string;
};

export const formatLastUsableDate = (
  expiresAt: string,
  timeZone: string
): string => {
  const lastUsableInstant = new Date(new Date(expiresAt).getTime() - 1);
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone,
  }).formatToParts(lastUsableInstant);

  let day = 1;
  let month = 1;
  let year = 1970;

  parts.forEach((part) => {
    if (part.type === "day") day = Number(part.value);
    if (part.type === "month") month = Number(part.value);
    if (part.type === "year") year = Number(part.value);
  });

  return format(new Date(year, month - 1, day), "MMMM d, yyyy");
};

export const ExpiringPointsSchedule = ({
  expirations,
  pointTerminologyOther,
  timeZone,
}: ExpiringPointsScheduleProps) => {
  if (expirations.length === 0) return null;

  const totalExpiringPoints = expirations.reduce(
    (total, expiration) => total + expiration.points,
    0
  );

  return (
    <VStack
      gap={4}
      backgroundColor="surface-secondary"
      borderRadius="4"
      padding={4}
      testID="loyalty-expiring-points-schedule"
    >
      <VStack gap={1}>
        <HStack alignItems="center" gap={3}>
          <Text
            flex={1}
            variant="mainHeading"
            fontWeight="bold"
            color="text-primary"
          >
            {t`${pointTerminologyOther} Expiring Soon`}
          </Text>
          <ExpiringPointsPill points={totalExpiringPoints} />
        </HStack>
        <Text variant="mainSmallMedium" color="text-secondary">
          {t`Use these ${pointTerminologyOther} before they expire.`}
        </Text>
      </VStack>
      <VStack gap={3}>
        {expirations.map((expiration) => (
          <HStack
            key={expiration.id}
            alignItems="center"
            justifyContent="space-between"
            borderRadius="2"
            backgroundColor="surface-primary"
            px={4}
            py={2}
          >
            <Text variant="mainBaseMedium" color="text-primary">
              {formatLastUsableDate(expiration.expiresAt, timeZone)}
            </Text>
            <HStack alignItems="center" gap={2}>
              <Text variant="mainBaseSemiBold" color="text-primary">
                {expiration.points}
              </Text>
              <LoyaltyIcon color="icon-primary" width={16} height={16} />
            </HStack>
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
};

const ExpiringPointsPill = ({ points }: { points: number }) => (
  <HStack
    alignItems="center"
    justifyContent="center"
    alignSelf="flex-start"
    gap={2}
    px={4}
    py={1}
    backgroundColor="surface-primary"
    style={{ borderRadius: 100 }}
  >
    <Text variant="mainSmallSemiBold" color="text-primary">
      {points}
    </Text>
    <LoyaltyIcon color="icon-primary" width={16} height={16} />
  </HStack>
);
